// ---------------------------------------------------------------------------
// AgentDetailView — center-pane detail for a working copy or published
// snapshot. A right rail lists immutable version snapshots.
// ---------------------------------------------------------------------------

import {
  Body1,
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Spinner,
  Tab,
  TabList,
  Tooltip,
} from '@fluentui/react-components';
import {
  Delete20Filled,
  Delete20Regular,
  Dismiss20Filled,
  Dismiss20Regular,
  Edit20Filled,
  Edit20Regular,
  History20Filled,
  History20Regular,
  MoreHorizontal20Filled,
  MoreHorizontal20Regular,
  Play20Filled,
  Play20Regular,
  Save20Filled,
  Save20Regular,
  bundleIcon,
} from '@fluentui/react-icons';
import { useCallback, useEffect, useReducer, useState } from 'react';

import { ConfirmDeleteDialog } from '../../../components/ConfirmDeleteDialog/ConfirmDeleteDialog.js';
import { VersionsRail } from '../../../components/VersionsRail/VersionsRail.js';
import { VersionStatusBadge } from '../../../components/VersionStatusBadge/index.js';
import { useAgent } from '../../../hooks/useAgent/useAgent.js';
import { useAgentMutations } from '../../../hooks/useAgentMutations/useAgentMutations.js';
import { useAgentVersions } from '../../../hooks/useAgentVersions/useAgentVersions.js';
import { useCanEditResource } from '../../../hooks/useCanEditResource/useCanEditResource.js';
import { getAgentDisplayName, isLlmSpec } from '../../../types/agent.js';
import type { AgentVersion } from '../../../types/agent.js';
import { AgentForm, type AgentEditControls } from '../AgentForm/AgentForm.js';

import {
  agentDetailViewReducer,
  initialAgentDetailViewState,
  type AgentDetailTab,
} from './AgentDetailView.reducer.js';
import { useStyles } from './AgentDetailView.style.js';
import {
  AgentEvaluateTab,
  type AgentEvaluateControls,
} from './AgentEvaluateTab/AgentEvaluateTab.js';
import { AgentSpecDetails } from './AgentSpecDetails.js';
import { HttpAgentForm } from './HttpAgentForm.js';
import { VersionSnapshotDialog } from './VersionSnapshotDialog.js';

/* eslint-disable @typescript-eslint/no-unsafe-assignment */
const EditIcon = bundleIcon(Edit20Filled, Edit20Regular);
const DeleteIcon = bundleIcon(Delete20Filled, Delete20Regular);
const DismissIcon = bundleIcon(Dismiss20Filled, Dismiss20Regular);
const MoreIcon = bundleIcon(MoreHorizontal20Filled, MoreHorizontal20Regular);
const HistoryIcon = bundleIcon(History20Filled, History20Regular);
const PlayIcon = bundleIcon(Play20Filled, Play20Regular);
const SaveIcon = bundleIcon(Save20Filled, Save20Regular);
/* eslint-enable @typescript-eslint/no-unsafe-assignment */

export interface AgentDetailViewProps {
  workspaceName: string;
  agentName: string;
  publishedVersion?: AgentVersion;
  /** Called after a successful delete so the parent can refresh / reselect. */
  onDeleted?: (name: string) => void;
  /** Called after a successful edit so the parent can refresh its list. */
  onChanged?: () => void;
}

export function AgentDetailView({
  workspaceName,
  agentName,
  publishedVersion,
  onDeleted,
  onChanged,
}: AgentDetailViewProps) {
  const styles = useStyles();

  const {
    agent,
    isLoading: loading,
    error: loadError,
    refresh: refreshAgent,
  } = useAgent({ workspaceName, agentName });

  const [ui, dispatch] = useReducer(
    agentDetailViewReducer,
    initialAgentDetailViewState,
  );
  const { detailTab, formMode, deleteOpen, showVersions, viewedVersion } = ui;
  const activeDetailTab =
    publishedVersion !== undefined ? 'overview' : detailTab;

  // The Evaluate tab reports its run-button state up so the action lives in
  // this title bar (mirrors the Eval Template page's "Queue job" placement).
  const [evalControls, setEvalControls] =
    useState<AgentEvaluateControls | null>(null);
  const [editControls, setEditControls] = useState<AgentEditControls | null>(
    null,
  );
  const [snapshotting, setSnapshotting] = useState(false);
  const [snapshottedVersion, setSnapshottedVersion] = useState<number | null>(
    null,
  );
  const [snapshotError, setSnapshotError] = useState<string | null>(null);

  // Reset view-local state whenever the loaded agent changes so a re-selection
  // never carries over the previous agent's edit/expanded state.
  useEffect(() => {
    dispatch({ type: 'RESET' });
    setSnapshottedVersion(null);
    setSnapshotError(null);
  }, [agentName]);

  useEffect(() => {
    if (publishedVersion !== undefined) {
      dispatch({ type: 'CANCEL_EDIT' });
    }
  }, [publishedVersion]);

  useEffect(() => {
    setSnapshottedVersion(null);
  }, [agent]);

  const {
    deleteAgent,
    snapshotAgent,
    isSaving: mutationSaving,
    error: deleteError,
  } = useAgentMutations({ workspaceName });

  const {
    versions,
    isLoading: versionsLoading,
    error: versionsError,
    refresh: refreshVersions,
  } = useAgentVersions({
    workspaceName,
    // Version metadata powers the title badge even while the rail is hidden.
    agentName,
  });

  const handleEditSaved = useCallback(() => {
    // PUT returns the new working copy; re-fetch so the hook stays the single
    // source of truth, then drop back to read-only and refresh the parent list.
    dispatch({ type: 'CANCEL_EDIT' });
    setSnapshottedVersion(null);
    refreshAgent();
    onChanged?.();
  }, [refreshAgent, onChanged]);

  const handleDelete = useCallback(async () => {
    try {
      await deleteAgent(agentName);
      dispatch({ type: 'SET_DELETE_OPEN', open: false });
      onDeleted?.(agentName);
    } catch {
      // deleteError is surfaced inside the dialog.
    }
  }, [deleteAgent, agentName, onDeleted]);

  const handleSnapshot = useCallback(async () => {
    setSnapshotError(null);
    setSnapshotting(true);
    try {
      const snapshot = await snapshotAgent(agentName);
      setSnapshottedVersion(snapshot.version);
      refreshAgent();
      refreshVersions();
      onChanged?.();
    } catch (err) {
      setSnapshotError(
        err instanceof Error ? err.message : 'Failed to snapshot agent.',
      );
    } finally {
      setSnapshotting(false);
    }
  }, [agentName, snapshotAgent, refreshAgent, refreshVersions, onChanged]);

  const ready = loadError === null && agent !== null;
  // Edit/delete gating — see canEditResource.
  const canEdit = useCanEditResource({
    workspaceName,
    resourceCreatedBy: agent?.systemProperties?.createdBy,
  });
  const latestVersion =
    publishedVersion?.version ??
    snapshottedVersion ??
    versions[versions.length - 1]?.version;
  const isDraft =
    publishedVersion === undefined &&
    snapshottedVersion === null &&
    agent?.isDraft === true;
  const canEditSpec =
    agent !== null &&
    (isLlmSpec(agent.spec) || agent.spec?.agentType === 'AgentHttpProxySpec');

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <span className={styles.name}>
            {publishedVersion
              ? getAgentDisplayName(publishedVersion)
              : agent
                ? getAgentDisplayName(agent)
                : agentName}
          </span>
          {ready && (
            <VersionStatusBadge isDraft={isDraft} version={latestVersion} />
          )}
        </div>
        <div className={styles.actions}>
          {ready && formMode === 'view' && activeDetailTab === 'overview' && (
            <>
              {canEdit && publishedVersion === undefined && (
                <>
                  {canEditSpec && (
                    <Button
                      appearance="secondary"
                      icon={<EditIcon />}
                      onClick={() => dispatch({ type: 'EDIT' })}
                    >
                      Edit
                    </Button>
                  )}
                  {isDraft && (
                    <Button
                      appearance="secondary"
                      icon={<SaveIcon />}
                      onClick={() => void handleSnapshot()}
                      disabled={snapshotting}
                    >
                      {snapshotting ? 'Publishing…' : 'Publish'}
                    </Button>
                  )}
                  <Button
                    appearance="secondary"
                    icon={<DeleteIcon />}
                    onClick={() =>
                      dispatch({ type: 'SET_DELETE_OPEN', open: true })
                    }
                  >
                    Delete
                  </Button>
                </>
              )}
              <Menu>
                <MenuTrigger disableButtonEnhancement>
                  <Tooltip content="More actions" relationship="label">
                    <MenuButton
                      appearance="secondary"
                      icon={<MoreIcon />}
                      aria-label="More actions"
                    />
                  </Tooltip>
                </MenuTrigger>
                <MenuPopover>
                  <MenuList>
                    <MenuItem
                      icon={<HistoryIcon />}
                      onClick={() => dispatch({ type: 'TOGGLE_VERSIONS' })}
                    >
                      {showVersions
                        ? 'Hide version history'
                        : 'Version history'}
                    </MenuItem>
                  </MenuList>
                </MenuPopover>
              </Menu>
            </>
          )}
          {ready &&
            publishedVersion === undefined &&
            formMode === 'edit' &&
            activeDetailTab === 'overview' && (
              <>
                <Button
                  appearance="subtle"
                  icon={<DismissIcon />}
                  onClick={() => dispatch({ type: 'CANCEL_EDIT' })}
                  disabled={editControls?.saving ?? false}
                >
                  Cancel
                </Button>
                <Button
                  appearance="primary"
                  icon={<SaveIcon />}
                  onClick={() => editControls?.save()}
                  disabled={!editControls?.canSave}
                >
                  {editControls?.saving ? 'Saving…' : 'Save'}
                </Button>
              </>
            )}
          {activeDetailTab === 'evaluate' && (
            <Button
              appearance="primary"
              icon={<PlayIcon />}
              onClick={() => evalControls?.run()}
              disabled={
                evalControls === null ||
                !evalControls.canRun ||
                evalControls.running
              }
            >
              {evalControls?.running ? 'Starting…' : 'Run evaluation'}
            </Button>
          )}
        </div>
      </div>

      <div className={styles.tabs}>
        <TabList
          selectedValue={activeDetailTab}
          onTabSelect={(_, d) =>
            dispatch({ type: 'SET_DETAIL_TAB', tab: d.value as AgentDetailTab })
          }
          size="medium"
        >
          <Tab value="overview">Overview</Tab>
          {publishedVersion === undefined && (
            <Tab value="evaluate">Evaluate</Tab>
          )}
        </TabList>
      </div>

      {activeDetailTab === 'evaluate' ? (
        <AgentEvaluateTab
          key={agentName}
          workspaceName={workspaceName}
          agentName={agentName}
          onControlsChange={setEvalControls}
        />
      ) : (
        <div className={styles.body}>
          <div className={styles.formColumn}>
            {loading && agent === null ? (
              <div className={styles.spinnerWrap}>
                <Spinner label="Loading agent..." />
              </div>
            ) : loadError !== null || agent === null ? (
              <div className={styles.spinnerWrap}>
                <Body1 className={styles.errorText}>
                  {loadError ?? 'Agent not found.'}
                </Body1>
              </div>
            ) : (
              <>
                {snapshotError !== null && (
                  <Body1 className={styles.errorText} role="alert">
                    {snapshotError}
                  </Body1>
                )}
                {publishedVersion !== undefined ? (
                  <AgentSpecDetails agent={publishedVersion} />
                ) : formMode === 'edit' &&
                  agent.spec?.agentType === 'AgentHttpProxySpec' ? (
                  <HttpAgentForm
                    key={agent.name}
                    agent={agent}
                    spec={agent.spec}
                    workspaceName={workspaceName}
                    onEditSaved={handleEditSaved}
                    onEditControlsChange={setEditControls}
                  />
                ) : formMode === 'edit' && isLlmSpec(agent.spec) ? (
                  <AgentForm
                    key={`${agent.name}:${formMode}`}
                    mode={formMode}
                    initialAgent={agent}
                    onEditSaved={handleEditSaved}
                    onEditCancelled={() => dispatch({ type: 'CANCEL_EDIT' })}
                    onEditControlsChange={setEditControls}
                  />
                ) : (
                  <AgentSpecDetails agent={agent} />
                )}
              </>
            )}
          </div>

          {showVersions && (
            <VersionsRail
              versions={versions}
              loading={versionsLoading}
              error={versionsError}
              onHide={() => dispatch({ type: 'HIDE_VERSIONS' })}
              onViewVersion={(v) =>
                dispatch({ type: 'VIEW_VERSION', version: v })
              }
            />
          )}
        </div>
      )}

      <ConfirmDeleteDialog
        open={deleteOpen}
        title="Delete agent"
        deleting={mutationSaving && deleteOpen}
        error={deleteError}
        onOpenChange={(open) => dispatch({ type: 'SET_DELETE_OPEN', open })}
        onConfirm={() => void handleDelete()}
      >
        Delete <strong>{agentName}</strong> and all of its version snapshots?
        This cannot be undone.
      </ConfirmDeleteDialog>

      <VersionSnapshotDialog
        version={viewedVersion}
        onClose={() => dispatch({ type: 'CLOSE_VERSION' })}
      />
    </div>
  );
}
